import { useState } from 'react';
import { StylesConfig } from 'react-select';
import ReactAsyncSelect from 'react-select/async';

import classNames from 'classnames/bind';
import { ShadowStyle } from 'consts/shadow';
import { getInputBorderStyle } from 'lib/BaseInput';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import styles from 'lib/AsyncSelect/AsyncSelect.pcss';
import { asyncSelectThemeStyles, groupHeadingStyles } from 'lib/AsyncSelect/AsyncSelect.styles';
import { AsyncSelectInput, AsyncSelectProps } from 'lib/AsyncSelect/AsyncSelect.types';

const cx = classNames.bind(styles);

const AsyncSelect = <T extends {}>({
  closeMenuOnSelect = true,
  customComponents = {},
  dataTestId,
  debounceTime: debounceTimeInSeconds = 1,
  defaultOptions = [],
  hideSelectedOptions = true,
  isClearable = true,
  isDisabled = false,
  isMultiValueVisible = true,
  loadData,
  isMulti,
  multiple = false,
  menuPosition = 'absolute',
  menuPlacement = 'bottom',
  name,
  onMenuScrollToBottom,
  onSelect,
  placeholder = 'Select...',
  selectedValue = null,
  shadow = ShadowStyle.NONE,
  shape = InputShape.ROUNDED_MEDIUM,
  textKey = 'name',
  size = InputSize.MEDIUM,
  valueKey = 'code',
  theme = InputTheme.LIGHT,
  dataPendoId = '',
  noOptionsMessage = undefined,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: AsyncSelectProps<T>): React.ReactElement => {
  let timeOut: NodeJS.Timeout | null = null;
  const [fetchedOptions, setFetchedOptions] = useState<T[]>([]);
  const [areNoResults, setAreNoResults] = useState(false);

  const loadOptions = (inputValue: string): Promise<T[]> => {
    if (timeOut) {
      clearTimeout(timeOut);
    }

    setAreNoResults(false);

    return new Promise((resolve) => {
      timeOut = setTimeout(async () => {
        const data = await loadData(name, inputValue);

        resolve(data);
        setFetchedOptions(data);
        setAreNoResults(data.length === 0);
      }, debounceTimeInSeconds * 1000);
    });
  };

  const handleChange = (newValue: T): void => {
    onSelect(name, newValue);
  };

  const getOptionLabel = (option: T): string => {
    return String(option[textKey as keyof T] ?? '');
  };

  const getOptionValue = (option: T): string => {
    return String(option[valueKey as keyof T] ?? '');
  };

  const getNoOptionsMessage = (): string => {
    if (areNoResults) {
      return 'No results';
    }

    return 'Type to search';
  };

  const getLoadingMessage = (options: AsyncSelectInput): string => {
    return `Searching for '${options.inputValue}'`;
  };

  const getStyles = (): StylesConfig => {
    const isAutoComplete = defaultOptions && defaultOptions?.length > 0;

    return {
      control: (base) => ({
        ...base,
        ...asyncSelectThemeStyles.control[theme],
        ...asyncSelectThemeStyles.size.control[size],
      }),
      input: (base) => ({
        ...base,
        ...asyncSelectThemeStyles.input[theme],
        ...asyncSelectThemeStyles.size.input[size],
      }),
      multiValue: (base) => ({
        ...base,
        display: isMultiValueVisible ? base.display : 'none',
      }),
      indicatorSeparator: (base) => ({
        ...base,
        display: isAutoComplete ? base.display : 'none',
      }),
      dropdownIndicator: (base) => ({
        ...base,
        display: isAutoComplete ? base.display : 'none',
      }),
      groupHeading: (base) => ({
        ...base,
        ...groupHeadingStyles,
      }),
      menuList: (base) => ({
        ...base,
        overflowX: 'hidden',
      }),
      option: (base) => ({
        ...base,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }),
    };
  };

  return (
    <div
      className={cx(
        getInputBorderStyle({
          isDisabled,
          shape,
          theme,
        }),
      )}
      data-test-id={dataTestId}
      data-pendo-id={dataPendoId}
    >
      <ReactAsyncSelect
        backspaceRemovesValue={isMultiValueVisible}
        closeMenuOnSelect={closeMenuOnSelect}
        className={cx(
          'async-select',
          'w-full',
          { 'shadow-inner-sm': shadow !== ShadowStyle.NONE },
          { 'shadow-none': shadow === ShadowStyle.NONE },
          { 'async-select': theme === InputTheme.LIGHT },
          { 'async-select-flat-gray': theme === InputTheme.FLAT_GRAY },
          { 'async-select-white-with-border': theme === InputTheme.WHITE_WITH_BORDER },
        )}
        classNamePrefix="react-select"
        components={customComponents}
        defaultOptions={fetchedOptions.length ? fetchedOptions : defaultOptions}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hideSelectedOptions={hideSelectedOptions}
        isClearable={isClearable}
        id={`react-select-${name}`}
        isDisabled={isDisabled}
        isMulti={isMulti ?? multiple}
        loadingMessage={getLoadingMessage}
        loadOptions={loadOptions}
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        noOptionsMessage={noOptionsMessage ?? getNoOptionsMessage}
        onChange={handleChange}
        onMenuClose={() => setFetchedOptions([])}
        onMenuScrollToBottom={onMenuScrollToBottom}
        placeholder={placeholder}
        styles={getStyles()}
        value={selectedValue}
      />
    </div>
  );
};

export default AsyncSelect;
